import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

type User = {
  id: string;
  name: string;
  email: string;
  password2: string;
  actual_total_deposit_amount: number;
  total_withdrawal_usdt: number;
  currency?: string;
  [key: string]: any;
};

const getCurrencySymbol = (currency?: string) => {
  if (currency === "PKR") return "₨";
  if (currency === "BDT") return "৳";
  if (currency === "INR") return "₹";
  if (currency === "USD") return "$";
  return currency ? `${currency} ` : "$";
};

const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";

  const day = date.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
};

const apiBase = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:3006"
  : "https://api.trubetx.com";

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [debitValues, setDebitValues] = useState({});
  const [creditValues, setCreditValues] = useState({});

  const [depositWithdrawalData, setDepositWithdrawalData] = useState({
    labels: [],
    datasets: [
      {
        label: "Total Deposits",
        data: [],
        backgroundColor: "rgba(79, 70, 229, 0.6)",
        borderColor: "rgba(79, 70, 229, 1)",
        borderWidth: 1,
      },
      {
        label: "Total Withdrawals",
        data: [],
        backgroundColor: "rgba(156, 163, 175, 0.6)",
        borderColor: "rgba(156, 163, 175, 1)",
        borderWidth: 1,
      },
    ],
  });

  const [userActivityData, setUserActivityData] = useState({
    labels: ["Active Users", "Inactive Users"],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ["rgba(79, 70, 229, 0.8)", "rgba(156, 163, 175, 0.8)"],
        borderColor: ["rgba(79, 70, 229, 1)", "rgba(156, 163, 175, 1)"],
        borderWidth: 1,
      },
    ],
  });

  
const [page, setPage] = useState(1);
const [limit] = useState(5); // or make it dynamic if needed
const [totalPages, setTotalPages] = useState(1);
const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

const toggleSortOrder = () => {
  setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
};


 useEffect(() => {
  fetchUsersData(page, limit);
}, [page, searchTerm, sortOrder]); // will re-fetch on page change

  const fetchUsersData = async (page, limit) => {
  try {
    const response = await axios.get(
      `${apiBase}/getUserData?page=${page}&limit=${limit}&search=${searchTerm}&sortOrder=${sortOrder}`,
    );
    const data = response.data;

    if (Array.isArray(data?.users)) {
      const reversedData = data.users;
      setUsers(reversedData);
      setPage(data.page);
      setTotalPages(data.totalPages || 1); // from backend

      // Chart and pie logic (no change)...
       // Prepare data for the deposit/withdrawal chart
        const labels = reversedData.slice(0, 7).map((user) => user.name);
        const deposits = reversedData
          .slice(0, 7)
          .map((user) => user.actual_total_deposit_amount);
        const withdrawals = reversedData
          .slice(0, 7)
          .map((user) => user.total_withdrawal_usdt);

        setDepositWithdrawalData({
          labels,
          datasets: [
            {
              label: "Total Deposits",
              data: deposits,
              backgroundColor: "rgba(79, 70, 229, 0.6)",
              borderColor: "rgba(79, 70, 229, 1)",
              borderWidth: 1,
            },
            {
              label: "Total Withdrawals",
              data: withdrawals,
              backgroundColor: "rgba(156, 163, 175, 0.6)",
              borderColor: "rgba(156, 163, 175, 1)",
              borderWidth: 1,
            },
          ],
        });

        // Prepare data for the user activity pie chart
        const activeUsers = reversedData.filter(
          (user) => user.actual_total_deposit_amount > 0
        ).length;
        const inactiveUsers = reversedData.length - activeUsers;

        setUserActivityData({
          labels: ["Active Users", "Inactive Users"],
          datasets: [
            {
              data: [activeUsers, inactiveUsers],
              backgroundColor: [
                "rgba(79, 70, 229, 0.8)",
                "rgba(156, 163, 175, 0.8)",
              ],
              borderColor: ["rgba(79, 70, 229, 1)", "rgba(156, 163, 175, 1)"],
              borderWidth: 1,
            },
          ],
        });
    } else {
      console.error("Unexpected response structure:", data);
      setUsers([]);
    }
  } catch (error) {
    console.error("Failed to fetch user data:", error);
    setUsers([]);
  }
};


  const handleInputChange = (uid, value, type) => {
    if (type === "debit") {
      setDebitValues((prev) => ({ ...prev, [uid]: value }));
    } else if (type === "credit") {
      setCreditValues((prev) => ({ ...prev, [uid]: value }));
    }
  };

  // Function to handle the API call for both debit and credit actions
  const handleCreditDebit = async (uid, action) => {
    // Get the input amount based on action: "add" for credit, "subtract" for debit
    const amountStr =
      action === "credit" ? creditValues[uid] : debitValues[uid];
    const amount = parseFloat(amountStr);

    if (!amount || isNaN(amount) || amount <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    try {
      const token = localStorage.getItem("token"); // Retrieve token if needed

      // Call the update balance API
      const response = await axios.post(
        `${apiBase}/updatebalance`,
        {
          uid,
          coin: "inr", // Pass coin as "inr"; adjust if needed
          amount, // Amount as a number
          type: action, // "add" or "subtract"
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert(response.data.message);

      // Clear the input field for the corresponding action
      if (action === "credit") {
        setCreditValues((prev) => ({ ...prev, [uid]: "" }));
      } else {
        setDebitValues((prev) => ({ ...prev, [uid]: "" }));
      }

      // Optionally, refresh your user data or update the UI accordingly
      // fetchUsersData();
    } catch (error) {
      console.error("Error updating wallet balance:", error);
      alert("Failed to update balance.");
    }
  };

  const handleDeleteUser = async (id: string, name: string) => {
    try {
      await axios.delete(`${apiBase}/deleteUser`, {
        data: { id },
      });
      fetchUsersData();
      setSnackbar(`User ${name} deleted successfully`);
      setTimeout(() => setSnackbar(null), 3000); // Hide snackbar after 3 seconds
    } catch (error) {
      console.error("Failed to delete user:", error);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    uid: "",
    username: "",
    password: "",
  });

  const handleOpenModal = (user) => {
    setEditUserData({ uid: user.id, username: user.name, password: "" }); // Pre-fill username
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditUserData({ uid: "", username: "", password: "" });
  };

  const handleEditUser = async () => {
    try {
      await axios.put(`${apiBase}/editProfile`, {
        uid: editUserData.uid,
        username: editUserData.username,
        password: editUserData.password || undefined, // Only send password if entered
      });

      fetchUsersData(); // Refresh data
      setSnackbar(`User ${editUserData.username} edited successfully`);
      setTimeout(() => setSnackbar(null), 3000);
      handleCloseModal();
    } catch (error) {
      console.error("Failed to edit user:", error);
    }
  };

  const filteredUsers = searchTerm
    ? users.filter(
        (user) =>
          user.id.toString().includes(searchTerm) ||
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  const sortedUsers = [...users].sort((a, b) => {
    if (!a.created) return 1;
    if (!b.created) return -1;
    const dateA = new Date(a.created).getTime();
    const dateB = new Date(b.created).getTime();
    if (isNaN(dateA)) return 1;
    if (isNaN(dateB)) return -1;
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  return (
    <div className="ml-0 p-2 lg:p-0 lg:ml-[256px] h-screen ">
      <h1 className="text-3xl font-bold mb-8 text-[#292524]">Users</h1>

      {snackbar && (
        <div className="mb-4 text-center text-white bg-[#228BE6] py-2 px-4 rounded-lg shadow-md">
          {snackbar}
        </div>
      )}
      <div className="mb-5">
        <div className="flex items-center justify-between p-4 bg-[#ffffff] dark:bg-[#F9FAFB] rounded-t-lg">
          <h2 className="text-xl font-bold text-black">
            {`Total Users: ${users.length}`}
          </h2>
          <div className=" flex justify-end items-center">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="">
              <input
                type="text"
                id="search"
                className="bg-[#ffffff] w-full pl-3 pr-4 py-2 rounded-lg border  shadow-sm focus:outline-none text-black"
                placeholder="Search by email, name or UID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-500"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto custom-scrollbar shadow-md sm:rounded-lg bg-white">
          <table className="text-sm text-left text-gray-500 w-full">
            <thead className="text-m text-white capitalize bg-[#394a6a] top-0 z-5">
              <tr>
                <th scope="col" className="py-3 px-6">
                  S.No
                </th>
                <th 
                  scope="col" 
                  className="py-3 px-6 text-nowrap cursor-pointer select-none hover:bg-[#2b3952]"
                  onClick={toggleSortOrder}
                >
                  <div className="flex items-center">
                    Date of Registration
                    <span className="inline-flex flex-col ml-2 justify-center">
                      <span className={`text-[8px] leading-none ${sortOrder === "asc" ? "text-white" : "text-gray-400"}`}>▲</span>
                      <span className={`text-[8px] leading-none ${sortOrder === "desc" ? "text-white" : "text-gray-400"}`}>▼</span>
                    </span>
                  </div>
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Phone no
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  IP Address
                </th>
                <th scope="col" className="py-3 px-6">
                  Name
                </th>
                <th scope="col" className="py-3 px-6">
                  Email
                </th>
                <th scope="col" className="py-3 px-6">
                  Password
                </th>
                <th scope="col" className="py-3 px-6">
                  UID
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Total Deposit
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Add Money By Admin
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Minus Money By Admin
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Total Withdrawal
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Referral Link
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Referral Code
                </th>
                <th scope="col" className="py-3 px-6 text-nowrap">
                  Referral By
                </th>
                <th scope="col" className="py-3 px-6">
                  Balance
                </th>
                <th scope="col" className="py-3 px-6">
                  Debit
                </th>
                <th scope="col" className="py-3 px-6">
                  Credit
                </th>
                <th scope="col" className="py-3 px-6">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user, index) => (
                <tr
                  key={user.id}
                  className="group bg-[#F9FAFB] border-b border-gray-200 "
                >
                  <td className="text-black py-4 px-6">{index + 1}</td>
                  <td className="text-black py-4 px-6 text-nowrap">{formatDate(user?.created)}</td>
                   <td className="text-black py-4 px-6">{user?.phone_no ? `${user?.countrycode || user?.countryCode || ''} ${user.phone_no}`.trim() : "N/A"}</td>
                  <td className="text-black py-4 px-6">{user?.ip || "N/A"}</td>
                 
                  <td className="text-black py-4 px-6">{user.name}</td>
                  <td className="text-black py-4 px-6">{user.email}</td>
                  <td className="text-black py-4 px-6">{user?.password2}</td>
                  <td className="text-black py-4 px-6">{user.id}</td>
                  <td className="  py-4 px-6 text-green-600">{`${getCurrencySymbol(user?.currency)}${
                    user?.actual_total_deposit_amount || 0
                  }`}</td>
                  <td className="  py-4 px-6 text-green-600">{`${getCurrencySymbol(user?.currency)}${
                    user?.add_money_by_admin || 0
                  }`}</td>
                  <td className="  py-4 px-6 text-red-600">{`${getCurrencySymbol(user?.currency)}${
                    user?.minus_money_by_admin | 0
                  }`}</td>
                  <td className="  py-4 px-6 text-red-600">{`${getCurrencySymbol(user?.currency)}${
                    user?.total_withdrawal_usdt || 0
                  }`}</td>

                  <td className="text-black py-4 px-6">{user.referral_link}</td>
                  <td className="text-black py-4 px-6">{user.referalcode}</td>
                  <td className="text-black py-4 px-6">{user.refree}</td>
                  <td className="text-black py-4 px-6">{user.available_inr}</td>
                  {/* Debit Column */}
                  <td className="text-black py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={debitValues[user.id] || ""}
                        onChange={(e) =>
                          handleInputChange(user.id, e.target.value, "debit")
                        }
                        placeholder="Enter amount"
                        className="p-2 border rounded-lg shadow-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
                      />
                      <button
                        onClick={() => handleCreditDebit(user.id, "debit")}
                        className="bg-red-500 text-white py-2 px-4 rounded-lg shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Debit
                      </button>
                    </div>
                  </td>

                  {/* Credit Column */}
                  <td className="text-black py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={creditValues[user.id] || ""}
                        onChange={(e) =>
                          handleInputChange(user.id, e.target.value, "credit")
                        }
                        placeholder="Enter amount"
                        className="p-2 border rounded-lg shadow-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
                      />
                      <button
                        onClick={() => handleCreditDebit(user.id, "credit")}
                        className="bg-[#228BE6] text-white py-2 px-4 rounded-lg shadow hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        Credit
                      </button>
                    </div>
                  </td>

                  <td className="py-4 px-6">
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        className="text-white bg-red-500 hover:bg-red-700 font-medium rounded-lg text-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => handleOpenModal(user)}
                        className="text-white bg-sky-500 hover:bg-sky-700 font-medium rounded-lg text-sm px-4 py-2"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* paginatoin */}
            <tfoot>
              <tr>
                <td colSpan={16} className="py-4 px-6 text-start">
                  <nav className="inline-flex items-center space-x-2">
                    <button
                  onClick={() => fetchUsersData(page - 1, limit)}
                  disabled={page <= 1}
                  className={`px-4 py-2 rounded ${
                    page <= 1
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  Previous
                    </button>
                    <span className="text-gray-700 px-4 py-2">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => fetchUsersData(page + 1, limit)}
                      disabled={page >= totalPages}
                      className={`px-4 py-2 rounded ${
                        page >= totalPages
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </td>
              </tr>
            </tfoot>

            
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="bg-[#ffffff] p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4 text-black">
            Deposits vs Withdrawals
          </h2>
          <Bar
            data={depositWithdrawalData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "top",
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                  },
                  ticks: {
                    color: "black", // This line changes the x-axis label color to white
                  },
                  border: {
                    color: "black", // This changes the x-axis line color to white
                  },
                },
                y: {
                  beginAtZero: true,
                  grid: {
                    display: false,
                  },
                  ticks: {
                    color: "black", // This line changes the text color to white
                    callback: function (value) {
                      return (value as number).toLocaleString();
                    },
                    stepSize: 200000,
                    max: 1600000,
                  },
                  border: {
                    color: "black", // This changes the x-axis line color to white
                  },
                },
              },
            }}
          />
        </div>

        <div className="bg-[#ffffff] p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4 text-black">
            User Activity
          </h2>
          <div className="flex items-center justify-center">
            <div style={{ width: "250px", height: "250px" }}>
              <Pie
                data={userActivityData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h2 className="text-xl font-semibold mb-4">Edit User</h2>
            <label className="block text-sm font-medium text-gray-700">
              Username
            </label>
            <input
              type="text"
              value={editUserData.username}
              onChange={(e) =>
                setEditUserData({ ...editUserData, username: e.target.value })
              }
              className="w-full p-2 border rounded mb-3"
            />

            <label className="block text-sm font-medium text-gray-700">
              New Password (optional)
            </label>
            <input
              type="password"
              placeholder="Enter new password"
              value={editUserData.password}
              onChange={(e) =>
                setEditUserData({ ...editUserData, password: e.target.value })
              }
              className="w-full p-2 border rounded mb-3"
            />

            <div className="flex justify-end">
              <button
                onClick={handleCloseModal}
                className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
